import { useTenant } from '@emerald-works-nova/auth'
import React, { createContext, useState } from 'react'
import { useGroup, useProducts } from '../../hooks'
import { ALL_ADMIN, ALL_ENTERPRISE, BROWSER, CONSUMER } from '../../services/cognito/userGroups'
import consts from '../../consts'
import { useSession } from '@emerald-works/react-auth'
import { useSelector } from 'react-redux'
import { contentSlice } from '../../reducers'
import MindsetAgent from '.'
import { useEvent } from '@emerald-works/react-event-bus-client'

const MindsetContext = createContext()

const MindsetProvider = ({ children }) => {
  const tenant = useTenant()
  const mindsetAgentRef = React.useRef(null)

  const isPreview = useSelector(contentSlice.selectors.selectIsPreview)
  const { isInGroup } = useGroup()
  const { isAuthenticated } = useSession()
  const { hasContentHubAccess } = useProducts()
  const isClubBrowserUser = tenant.pk === consts.DEFAULT_CLUB_PK && (isInGroup(BROWSER) || !isAuthenticated)
  const shouldShowMindsetAi = (isInGroup([...ALL_ENTERPRISE, ...ALL_ADMIN, CONSUMER]) || isClubBrowserUser) && hasContentHubAccess && tenant.features?.mindsetAi
  const [isOpen, setOpen] = useState(false)
  const [hasBeenOpened, setHasBeenOpened] = useState(false)

  const setOpenWithTracking = (open) => {
    if (!hasBeenOpened && open) {
      setHasBeenOpened(true)
    }
    setOpen(open)
  }

  return (
    <MindsetContext.Provider value={{ mindsetAgentRef, shouldShowMindsetAi, hasBeenOpened, shouldTrackOpenEvent: !isClubBrowserUser, isOpen, setOpen: setOpenWithTracking }}>
      <>
        {shouldShowMindsetAi && !isPreview && (
          <MindsetAgent
            open={isOpen}
            setOpen={setOpenWithTracking}
            ref={mindsetAgentRef}
          />
        )}
        {children}
      </>
    </MindsetContext.Provider>
  )
}

const useMindset = () => {
  const { mindsetAgentRef, shouldShowMindsetAi, shouldTrackOpenEvent, isOpen, hasBeenOpened } = React.useContext(MindsetContext)
  const [askMCoachView] = useEvent([{ eventName: 'askMCoachView' }])

  const openMindset = React.useCallback(({ initialQuestion, from }) => {
    if (!mindsetAgentRef.current || isOpen) {
      return
    }

    if (shouldTrackOpenEvent) {
      askMCoachView.trigger({
        from,
        initialQuestion
      })
    }

    mindsetAgentRef.current.open({ initialQuestion, from })
  }, [mindsetAgentRef, askMCoachView, isOpen, shouldTrackOpenEvent])

  const closeMindset = React.useCallback(() => {
    if (mindsetAgentRef.current) {
      mindsetAgentRef.current.close()
    }
  }, [mindsetAgentRef])

  return {
    openMindset,
    closeMindset,
    shouldShowMindsetAi,
    mindsetAgentRef,
    hasBeenOpened,
    isOpen
  }
}

export { MindsetProvider, useMindset }
